import { Fragment, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import BaseLayout from "src/layouts/BaseLayout"
import useLocalizedPath from "src/hooks/useLocalizedPath"

import "./Legal.css"

/* The privacy policy and the terms (2026-10-25). The text, its dates and the sections that depend
   on what is switched on live in gamekit and come from `GET /api/legal/<doc>/?lang=`; this screen
   only draws them. Plain text on the wire: a blank line is a paragraph, "- " lines are a list, and
   a bare URL or email becomes a link. Unlike a game screen it scrolls. */

const LINK = /(https?:\/\/[^\s)]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g

export const linkify = (text) =>
  text.split(LINK).map((part, i) => {
    if (i % 2 === 0) return part
    const trailing = part.match(/[.,;:]+$/)?.[0] ?? ""
    const target = trailing ? part.slice(0, -trailing.length) : part
    const href = target.includes("://") ? target : `mailto:${target}`
    return (
      <Fragment key={i}>
        <a href={href} target={target.includes("://") ? "_blank" : undefined} rel="noopener noreferrer">
          {target}
        </a>
        {trailing}
      </Fragment>
    )
  })

/** A section's body as blocks: paragraphs and lists, in order. */
export const blocksOf = (body) => {
  const blocks = []
  for (const chunk of String(body ?? "").split(/\n\s*\n/)) {
    const lines = chunk.split("\n").filter((line) => line.trim())
    let items = null
    let text = []
    const flush = () => {
      if (text.length) blocks.push({ type: "p", text: text.join(" ") })
      text = []
    }
    for (const line of lines) {
      if (line.startsWith("- ")) {
        if (!items) {
          flush()
          items = []
          blocks.push({ type: "ul", items })
        }
        items.push(line.slice(2).trim())
      } else {
        items = null
        text.push(line.trim())
      }
    }
    flush()
  }
  return blocks
}

const Legal = ({ doc }) => {
  const { t, i18n } = useTranslation()
  const localized = useLocalizedPath()
  const language = i18n.resolvedLanguage
  const [page, setPage] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setPage(null)
    setFailed(false)
    apiClient
      .get(`/legal/${doc}/`, { params: { lang: language } })
      .then((response) => !cancelled && setPage(response.data))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [doc, language])

  return (
    <BaseLayout>
      <main className="legal">
        <Link to={localized("/")} className="legal-back">
          {t("legal.back")}
        </Link>
        {failed && <p className="legal-state">{t("legal.failed")}</p>}
        {!page && !failed && <p className="legal-state">{t("legal.loading")}</p>}
        {page && (
          <article>
            <h1 className="legal-title">{page.title}</h1>
            <p className="legal-updated">{t("legal.updated", { date: page.updated })}</p>
            {page.sections.map((section, s) => (
              <section key={s} className="legal-section">
                <h2>{section.heading}</h2>
                {blocksOf(section.body).map((block, b) =>
                  block.type === "ul" ? (
                    <ul key={b}>
                      {block.items.map((item, i) => (
                        <li key={i}>{linkify(item)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p key={b}>{linkify(block.text)}</p>
                  )
                )}
              </section>
            ))}
          </article>
        )}
      </main>
    </BaseLayout>
  )
}

export default Legal
